import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API, { formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import AdvertiserLookup from "@/components/AdvertiserLookup";
import { RSpinBlock } from "@/components/RSpin";
import { presetToRange } from "@/components/DateRangePresets";
import { PublisherHeader } from "./publisher_detail/PublisherHeader";
import { TrackerSlotsPanel } from "./publisher_detail/TrackerSlotsPanel";
import { AssignedBrandsTable } from "./publisher_detail/AssignedBrandsTable";
import { DailyReportsApproval } from "./publisher_detail/DailyReportsApproval";
import { PostbackLogsTab } from "./publisher_detail/PostbackLogsTab";
import { PassthroughEditDialog } from "./publisher_detail/PassthroughEditDialog";
import { BrandCoveragePanel } from "./publisher_detail/BrandCoveragePanel";
import { ApiPreviewPanel } from "./publisher_detail/ApiPreviewPanel";
import { RoutedTrafficPanel } from "./publisher_detail/RoutedTrafficPanel";

const REPORTS_DEFAULT_PRESET = "last_7";
const reportsInitialRange = () => {
  const r = presetToRange(REPORTS_DEFAULT_PRESET);
  return { preset: REPORTS_DEFAULT_PRESET, start: r.start, end: r.end };
};

/**
 * Admin → Publisher Detail
 *
 * Top-level page that orchestrates per-publisher views: status header,
 * tracker slots, brand assignments, daily-report approvals, and postback
 * delivery logs. Each subsection is a small component under
 * `./publisher_detail/`; this file keeps state + data fetching + handlers
 * so the children stay pure presentational.
 */
export default function AdminPublisherDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [splitHistory, setSplitHistory] = useState([]);
  const [assignmentsEpoch, setAssignmentsEpoch] = useState(0);
  const [editingComm, setEditingComm] = useState(null);
  const [editForm, setEditForm] = useState({ base: "", pub: "", split: "" });
  // Split-only inline editor (Assigned Brands Split column).
  const [editingSplitBrandId, setEditingSplitBrandId] = useState(null);
  const [editingSplitValue, setEditingSplitValue] = useState("");
  const [savingSplit, setSavingSplit] = useState(false);
  // Passthrough-specific edit dialog state. Open when admin clicks the pencil
  // icon on a passthrough row in the Assigned Brands table.
  const [editingPassthrough, setEditingPassthrough] = useState(null);
  const [ptActions, setPtActions] = useState([{ action: "", value: "", note: "" }]);
  const [ptHeadline, setPtHeadline] = useState("");
  const [ptSplit, setPtSplit] = useState("");
  const [ptFlat, setPtFlat] = useState("");
  // Fixed-rate cap mode (Best-Buy-style). When ON, save switches the
  // assignment to `payout_mode = fixed_pct` with `custom_commission_pct
  // = ptCapPct`. Used for brands where you want to advertise a stable
  // pub rate even though the upstream network pays variable.
  const [ptCapMode, setPtCapMode] = useState(false);
  const [ptCapPct, setPtCapPct] = useState("");
  const [ptSaving, setPtSaving] = useState(false);
  const [dailyReports, setDailyReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsRange, setReportsRange] = useState(reportsInitialRange);
  const [expandedDate, setExpandedDate] = useState(null);
  const [selectedActionIds, setSelectedActionIds] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [postbackLogs, setPostbackLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(0);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsNetwork, setLogsNetwork] = useState("all");
  const [logsStatus, setLogsStatus] = useState("all");
  // Tracker slots — additional sub_ids attached to this publisher for use
  // across CPA networks. Each slot becomes a distinct sub_id1 in Impact /
  // Partnerize / Skimlinks / mrge / Yieldkit so a single publisher can run
  // multiple URLs to the same brand and have revenue attributed per URL.
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [creatingSlot, setCreatingSlot] = useState(false);

  const fetchSlots = useCallback(async () => {
    setSlotsLoading(true);
    try {
      const { data } = await API.get(`/admin/publishers/${id}/slots`);
      setSlots(data?.slots || []);
    } catch (err) { console.error(err); }
    finally { setSlotsLoading(false); }
  }, [id]);

  const addSlot = async () => {
    setCreatingSlot(true);
    try {
      const { data } = await API.post(`/admin/publishers/${id}/slots`);
      toast.success(`Slot ${data.label} created — sub_id ${data.sub_id}`);
      fetchSlots();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreatingSlot(false);
    }
  };

  const toggleSlot = async (slotSubId) => {
    try {
      const { data } = await API.patch(`/admin/publishers/${id}/slots/${slotSubId}`);
      toast.success(`Slot ${data.status}`);
      fetchSlots();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const fetchPostbackLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = { page: logsPage, page_size: 50 };
      if (logsNetwork !== "all") params.network = logsNetwork;
      if (logsStatus !== "all") params.status = logsStatus;
      const { data } = await API.get(`/admin/publishers/${id}/postback-logs`, { params });
      if (Array.isArray(data)) {
        setPostbackLogs(data);
        setLogsTotal(data.length);
        setLogsTotalPages(1);
      } else {
        setPostbackLogs(data.items || []);
        setLogsTotal(data.total || 0);
        setLogsTotalPages(data.total_pages || 0);
      }
    } catch (err) { console.error(err); }
    finally { setLoadingLogs(false); }
  }, [id, logsNetwork, logsStatus, logsPage]);

  // Per-day items cache — populated on first expand, served from memory
  // on subsequent expands so toggling stays instant.
  const [dayItemsCache, setDayItemsCache] = useState({});
  const [dayLoading, setDayLoading] = useState(null);

  const fetchDailyReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      // Rollup-only: items[] arrays are NOT shipped. Each day's items
      // are lazy-fetched on expand via `fetchDayItems` below. Cuts the
      // initial response for high-volume pubs from MBs to a few KBs.
      // Date filter defaults to last 7 days (incl. today); "All time"
      // uses a long days window so we never scan unbounded history.
      const params = {};
      if (reportsRange.start && reportsRange.end) {
        params.start_date = reportsRange.start;
        params.end_date = reportsRange.end;
      } else {
        params.days = 3650;
      }
      const { data } = await API.get(`/admin/publishers/${id}/daily-reports`, { params });
      setDailyReports(data.daily_reports || []);
      // Range change invalidates expanded day + item cache.
      setExpandedDate(null);
      setDayItemsCache({});
      setSelectedActionIds(new Set());
    } catch (err) { console.error(err); }
    finally { setLoadingReports(false); }
  }, [id, reportsRange.start, reportsRange.end]);

  const fetchDayItems = useCallback(async (day) => {
    if (!day || dayItemsCache[day]) return;
    setDayLoading(day);
    try {
      const { data } = await API.get(
        `/admin/publishers/${id}/daily-reports/${day}/items`
      );
      // Map {brand_name -> items[]} for O(1) lookup when merging into the
      // existing dailyReports[].brands[] rollup rows.
      const byBrand = {};
      for (const b of data.brands || []) byBrand[b.brand_name] = b.items || [];
      setDayItemsCache((prev) => ({ ...prev, [day]: byBrand }));
    } catch (err) {
      console.error(err);
      toast.error(formatApiError(err, "Failed to load day details"));
    } finally {
      setDayLoading(null);
    }
  }, [id, dayItemsCache]);

  const syncActionsFromImpact = async () => {
    setSyncing(true);
    try {
      const { data } = await API.post("/admin/actions/sync", {});
      const i = data.impact?.synced ?? 0;
      const p = data.partnerize?.synced ?? 0;
      const s = data.skimlinks?.synced ?? 0;
      toast.success(`Synced ${data.synced ?? 0} actions · ${i} Impact · ${p} Partnerize · ${s} Skimlinks`);
      await fetchDailyReports();
    } catch (err) {
      toast.error(formatApiError(err, "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  const fetchDetail = useCallback(async () => {
    try {
      const { data } = await API.get(`/admin/publishers/${id}`);
      setDetail(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  const bumpAssignments = useCallback(() => {
    setAssignmentsEpoch((n) => n + 1);
    fetchDetail();
  }, [fetchDetail]);

  const fetchSplitHistory = useCallback(async () => {
    try {
      const { data } = await API.get(`/admin/publishers/${id}/default-split-history`);
      setSplitHistory(data.history || []);
    } catch {
      setSplitHistory([]);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); fetchSlots(); fetchSplitHistory(); }, [fetchDetail, fetchSlots, fetchSplitHistory]);

  // Tabs are now controlled so Radix doesn't rely on a one-shot onClick that
  // silently fails when the tab is already active at mount (e.g. admin
  // navigated away and came back — component remounts but React keeps
  // state, or the user clicked Refresh and expects a fresh load). This
  // useEffect always re-fetches the tab's data on activation — bug-safe,
  // cheap, and lets `Refresh` work as expected.
  const [activeTab, setActiveTab] = useState("assign-brands");
  useEffect(() => {
    if (activeTab === "reports") fetchDailyReports();
    if (activeTab === "postback-logs") fetchPostbackLogs();
  }, [activeTab, fetchDailyReports, fetchPostbackLogs]);

  useEffect(() => { setLogsPage(1); }, [logsNetwork, logsStatus]);

  const publisherDefaultSplit = detail?.publisher?.default_split_pub_pct ?? 100;

  const handleLookupAssign = async ({ brand_ids, base, pub, split, payout_mode, pub_flat_amount, payout_actions, headline_payout, cpc_payout_display, error }) => {
    if (error) {
      toast.error(error);
      return;
    }
    try {
      const body = {
        publisher_id: id,
        pub_split_pct: split,
        brand_ids,
      };
      if (payout_mode) body.payout_mode = payout_mode;
      if (pub_flat_amount !== null && pub_flat_amount !== undefined) body.pub_flat_amount = pub_flat_amount;
      // Skimlinks + passthrough assignments don't have base / pub rates.
      if (pub !== null && pub !== undefined) body.custom_commission_pct = pub;
      if (base !== null && base !== undefined) body.base_commission_pct = base;
      // Passthrough breakdown — optional; blank mirrors brand catalog rates.
      if (payout_actions && payout_actions.length) body.payout_actions = payout_actions;
      if (headline_payout !== undefined) body.headline_payout = headline_payout;
      // CPC mode: optional display label (publisher-facing). The
      // backend bulk-assign also auto-detects offer_type=cpc on the
      // brand row and routes those into CPC mode regardless of the
      // selected payout_mode, so this field flows through both paths.
      if (cpc_payout_display !== undefined) body.cpc_payout_display = cpc_payout_display;

      const { data } = await API.post("/admin/publisher-brands/bulk-assign", body);
      if (data.assigned > 0) {
        toast.success(
          `Assigned ${data.assigned} brand${data.assigned === 1 ? "" : "s"}` +
            (data.skipped ? ` · ${data.skipped} skipped` : "")
        );
        // Show the first skip reason(s) so admin understands what didn't
        // make it through — especially important on single-brand manual
        // assigns where "1 skipped" with no detail looks like nothing
        // happened.
        if (data.skip_details && data.skip_details.length) {
          const first = data.skip_details[0];
          const more = data.skip_details.length > 1 ? ` (+ ${data.skip_details.length - 1} more)` : "";
          toast.warning(`Skipped: ${first.brand_name || first.brand_id} — ${first.reason}${more}`);
        }
      } else {
        // Nothing assigned. Always surface why so the admin can fix it.
        if (data.skip_details && data.skip_details.length) {
          const first = data.skip_details[0];
          const more = data.skip_details.length > 1 ? ` (+ ${data.skip_details.length - 1} more)` : "";
          toast.error(`Not assigned: ${first.brand_name || first.brand_id} — ${first.reason}${more}`);
        } else {
          toast.error(`No brands assigned · ${data.skipped} skipped`);
        }
      }
      bumpAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
  };

  // Smart Add — one-click auto-assign every rate-bearing brand from
  // the currently-active network filter (or all 3 core networks when
  // the pill is set to "All"). Always passthrough at the publisher's
  // configured default split (100% by default — no silent shave).
  // Idempotent.
  const [smartAdding, setSmartAdding] = useState(false);
  const [lookupNetwork, setLookupNetwork] = useState("all");
  // Pretty labels for the per-network Smart Add button.
  const NETWORK_LABELS_SMART = {
    impact: "Impact", skimlinks: "Skimlinks", mrge: "mrge",
    partnerize: "Partnerize", cj: "CJ", pubadhub: "PubAdHub",
    pubadhub_kevin: "PubAdHub 2", admedia: "AdMedia",
    flexoffers: "FlexOffers", shopnomix_kevin: "Shopnomix InMobi", siteplug: "Siteplug",
    maxbounty: "MaxBounty",
    cashbackhive: "CashbackHive",
    shopnomix: "Shopnomix", pronomix: "Pronomix",
  };
  const smartAssign = async () => {
    if (smartAdding) return;
    setSmartAdding(true);
    try {
      const payload = lookupNetwork !== "all"
        ? { networks: [lookupNetwork] }
        : {};
      const { data } = await API.post(`/admin/publishers/${id}/smart-assign`, payload);
      const by = data.assigned_by_network || {};
      const breakdown = Object.keys(by)
        .filter((n) => by[n])
        .map((n) => `${by[n]} ${n}`)
        .join(" · ");
      if (data.assigned_total > 0) {
        toast.success(
          `Smart-added ${data.assigned_total} brand${data.assigned_total === 1 ? "" : "s"} · ${breakdown || "all rate-bearing"} · ${data.skipped_already_assigned} already assigned${data.skipped_cashback ? ` · ${data.skipped_cashback} cashback skipped` : ""}`
        );
      } else {
        toast.info(
          `Nothing to add — ${data.skipped_already_assigned} already assigned, ${data.skipped_no_rates} no rates${data.skipped_cashback ? `, ${data.skipped_cashback} cashback (excluded)` : ""}`
        );
      }
      // When admin asks "why was *that* brand skipped?", surface the
      // first few reasons inline. Capped at 3 in the toast — the rest
      // are still in `data.skip_details` for debugging via DevTools.
      const others = (data.skip_details || []).filter(
        (s) => s.reason !== "already assigned" && s.reason !== "cashback (excluded from auto-arbitrage)"
      );
      if (others.length) {
        const sample = others.slice(0, 3).map(
          (s) => `${s.brand_name || s.brand_id} (${s.network}) — ${s.reason}`
        ).join("\n");
        const more = others.length > 3 ? `\n+ ${others.length - 3} more` : "";
        toast.warning(`Brands skipped:\n${sample}${more}`, { duration: 8000 });
      }
      bumpAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Smart-add failed");
    } finally {
      setSmartAdding(false);
    }
  };

  const toggleBrandStatus = async (brandId, currentStatus) => {
    try {
      await API.put(`/admin/publisher-brands/${id}/${brandId}/status`, { status: currentStatus === "active" ? "paused" : "active" });
      toast.success(`Brand ${currentStatus === "active" ? "paused" : "activated"}`);
      bumpAssignments();
    } catch { toast.error("Failed"); }
  };

  const updateCommission = async (brandId) => {
    try {
      await API.put(`/admin/publisher-brands/${id}/${brandId}/commission`, {
        custom_commission_pct: parseFloat(editForm.pub),
        base_commission_pct: editForm.base !== "" ? parseFloat(editForm.base) : undefined,
        pub_split_pct: editForm.split !== "" ? parseFloat(editForm.split) : undefined,
      });
      toast.success("Assignment updated");
      setEditingComm(null);
      bumpAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // Prefill the passthrough edit dialog when a row is selected for editing.
  useEffect(() => {
    if (!editingPassthrough) return;
    const rows = editingPassthrough.payout_actions;
    setPtActions(
      Array.isArray(rows) && rows.length
        ? rows.map((r) => ({
            action: r.action || "",
            value: r.value || "",
            note: r.note || "",
          }))
        : [{ action: "", value: "", note: "" }]
    );
    // For CPC rows we reuse `ptHeadline` as the CPC display label
    // (e.g. "upto $0.25") so the existing dialog state machine doesn't
    // need a third buffer. The dialog branch on `_cpcMode` hides the
    // payout-actions editor entirely.
    setPtHeadline(
      editingPassthrough.cpc_payout_display ||
        editingPassthrough.headline_payout || ""
    );
    setPtSplit(String(editingPassthrough.pub_split_pct ?? publisherDefaultSplit));
    setPtFlat(
      editingPassthrough.pub_flat_amount != null &&
        Number(editingPassthrough.pub_flat_amount) > 0
        ? String(editingPassthrough.pub_flat_amount)
        : ""
    );
    // Detect whether this assignment is already in fixed-rate cap mode.
    // `payout_mode = 'fixed_pct'` + a positive `custom_commission_pct`
    // means the admin previously toggled cap mode on. Prefill the cap
    // input and flip the toggle so re-edits don't reset to passthrough.
    const isCap =
      editingPassthrough.payout_mode === "fixed_pct" &&
      Number(editingPassthrough.custom_commission_pct || 0) > 0;
    setPtCapMode(isCap);
    setPtCapPct(isCap ? String(editingPassthrough.custom_commission_pct) : "");
  }, [editingPassthrough, publisherDefaultSplit]);

  const closePassthroughDialog = () => {
    if (ptSaving) return;
    setEditingPassthrough(null);
    setPtActions([{ action: "", value: "", note: "" }]);
    setPtHeadline("");
    setPtSplit("");
    setPtFlat("");
    setPtCapMode(false);
    setPtCapPct("");
  };

  const saveEditedPassthrough = async () => {
    if (!editingPassthrough) return;

    // Fixed-rate cap mode short-circuit — runs BEFORE the split validator
    // because cap mode doesn't use the split field. Writes payout_mode =
    // fixed_pct with custom_commission_pct = cap (Best-Buy-style math).
    if (!editingPassthrough._cpcMode && ptCapMode) {
      const cap = parseFloat(ptCapPct);
      if (!Number.isFinite(cap) || cap <= 0 || cap > 100) {
        toast.error("Pub rate cap must be between 0 and 100 (e.g. 8.6)");
        return;
      }
      setPtSaving(true);
      try {
        await API.put(
          `/admin/publisher-brands/${id}/${editingPassthrough.brand_id}/commission`,
          {
            payout_mode: "fixed_pct",
            custom_commission_pct: cap,
            // fixed_pct doesn't apply a split on top of the cap — send 100
            // (no-op) so the backend's 1-100 split validator is satisfied
            // and we don't accidentally re-shave via the split path.
            pub_split_pct: 100,
          }
        );
        toast.success(`Fixed-rate cap set to ${cap}%`);
        setEditingPassthrough(null);
        bumpAssignments();
      } catch (err) {
        toast.error(formatApiError(err, "Failed to set fixed-rate cap"));
      } finally {
        setPtSaving(false);
      }
      return;
    }

    const split = parseFloat(ptSplit);
    if (!Number.isFinite(split) || split <= 0 || split > 100) {
      toast.error("Split must be between 1 and 100");
      return;
    }

    // CPC short-circuit — split + display label + optional pub_flat_amount.
    if (editingPassthrough._cpcMode) {
      let flat = 0;
      if ((ptFlat || "").trim() !== "") {
        flat = parseFloat(ptFlat);
        if (!Number.isFinite(flat) || flat < 0) {
          toast.error("Pub flat $ must be 0 or greater");
          return;
        }
      }
      setPtSaving(true);
      try {
        await API.put(
          `/admin/publisher-brands/${id}/${editingPassthrough.brand_id}/commission`,
          {
            payout_mode: "cpc",
            custom_commission_pct: 0,
            pub_split_pct: split,
            pub_flat_amount: flat,
            cpc_payout_display: (ptHeadline || "").trim(),
          }
        );
        toast.success("CPC payout updated");
        setEditingPassthrough(null);
        bumpAssignments();
      } catch (err) {
        toast.error(formatApiError(err, "Failed to update CPC payout"));
      } finally {
        setPtSaving(false);
      }
      return;
    }

    const filled = ptActions.filter((r) => (r.value || "").trim());
    if (filled.length === 0 && !(ptHeadline || "").trim()) {
      toast.error(
        "Enter at least one payout action (e.g. 'Online Sale' → '35%-45%') or a headline value."
      );
      return;
    }
    setPtSaving(true);
    try {
      await API.put(
        `/admin/publisher-brands/${id}/${editingPassthrough.brand_id}/commission`,
        {
          payout_mode: "passthrough",
          custom_commission_pct: 0,
          pub_split_pct: split,
          payout_actions: filled,
          headline_payout: (ptHeadline || "").trim(),
        }
      );
      toast.success("Passthrough payout updated");
      setEditingPassthrough(null);
      bumpAssignments();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to update passthrough"));
    } finally {
      setPtSaving(false);
    }
  };

  const updateDefaultSplit = async () => {
    const newSplit = prompt(
      "Default split % for NEW assignments only (1-100).\nExisting offers keep their current split.",
      String(publisherDefaultSplit)
    );
    if (!newSplit) return;
    const parsed = parseFloat(newSplit);
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 100) {
      toast.error("Split must be between 1 and 100");
      return;
    }
    try {
      const { data } = await API.put(`/admin/publishers/${id}/default-split`, {
        default_split_pub_pct: parsed,
      });
      toast.success(
        data?.cascaded_assignments
          ? "Default split updated"
          : `Default split → ${parsed}/${100 - parsed} (new offers only)`
      );
      bumpAssignments();
      fetchSplitHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const saveBrandSplit = async (brandId) => {
    const parsed = parseFloat(editingSplitValue);
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 100) {
      toast.error("Split must be between 1 and 100");
      return;
    }
    setSavingSplit(true);
    try {
      await API.put(`/admin/publisher-brands/${id}/${brandId}/split`, {
        pub_split_pct: parsed,
      });
      toast.success(`Brand split → ${parsed}/${100 - parsed}`);
      setEditingSplitBrandId(null);
      setEditingSplitValue("");
      bumpAssignments();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to update split"));
    } finally {
      setSavingSplit(false);
    }
  };

  const toggleDiscloseNetworks = async (value) => {
    try {
      await API.put(`/admin/publishers/${id}/disclose-networks`, {
        disclose_networks: Boolean(value),
      });
      toast.success(
        value
          ? "Network names now disclosed to this publisher (API & UI)"
          : "Network names hidden from this publisher again"
      );
      bumpAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const updateTimezone = async (timezone) => {
    const current = detail?.publisher?.timezone || "UTC";
    if (!timezone || timezone === current) return;
    try {
      const { data } = await API.put(`/admin/publishers/${id}/timezone`, { timezone });
      const saved = data?.timezone || timezone;
      // Optimistic local merge so the picker doesn't flash back to UTC
      // while waiting on the full detail refetch.
      setDetail((prev) => prev ? {
        ...prev,
        publisher: { ...(prev.publisher || {}), timezone: saved },
      } : prev);
      toast.success(`Timezone updated to ${saved}`);
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update timezone");
      throw err;
    }
  };

  const updateClickCap = async (payload) => {
    // payload: { click_cap_per_day, auto_increment_enabled?, auto_increment_pct? }
    // Legacy callers may pass a bare int/null — normalise.
    const body =
      payload !== null && typeof payload === "object" && !Array.isArray(payload)
        ? payload
        : { click_cap_per_day: payload };
    try {
      const { data } = await API.put(`/admin/publishers/${id}/click-cap`, body);
      const saved = data?.click_cap_per_day ?? null;
      const summary = data?.click_cap_summary || null;
      setDetail((prev) => prev ? {
        ...prev,
        publisher: {
          ...(prev.publisher || {}),
          click_cap_per_day: saved,
          ...(summary ? { click_cap_summary: summary } : {}),
        },
      } : prev);
      const live = summary?.current_click_cap_per_day ?? saved;
      const label =
        live === null || live === undefined
          ? "Default (no limit)"
          : live === -1
            ? "No limit"
            : `${Number(live).toLocaleString()}/day`;
      toast.success(`Daily click cap reset to ${label}`);
      await fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update click cap");
      throw err;
    }
  };

  // Toggle a single network in the publisher's `disabled_networks` list.
  // When a network is added here, every external API surface
  // (`/v1/brands`, commission/conversion feeds, /click/...) hard-strips
  // brands on that network for this pub AND cascade-pauses their
  // publisher_brands offers on that network. Idempotent: clicking the
  // same chip removes it and restores cascade-paused offers.
  const toggleDisabledNetwork = async (networkLc) => {
    if (!networkLc) return;
    const current = Array.isArray(detail?.publisher?.disabled_networks)
      ? detail.publisher.disabled_networks
      : [];
    const isDisabled = current.includes(networkLc);
    const next = isDisabled
      ? current.filter((n) => n !== networkLc)
      : [...current, networkLc];
    try {
      const { data } = await API.put(`/admin/publishers/${id}/disabled-networks`, {
        disabled_networks: next,
      });
      const paused = data?.paused_assignments || 0;
      const unpaused = data?.unpaused_assignments || 0;
      toast.success(
        isDisabled
          ? `Re-enabled ${networkLc} — restored ${unpaused} offer${unpaused === 1 ? "" : "s"}`
          : `Disabled ${networkLc} — paused ${paused} offer${paused === 1 ? "" : "s"} + blocked API/tracker traffic`
      );
      bumpAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  // Build a set of action_ids that Skimlinks hasn't paid us for yet —
  // used for the confirm-prompt safety check before approval.
  const skimlinksUnpaidIds = useMemo(() => {
    const s = new Set();
    for (const day of dailyReports) {
      for (const b of day.brands || []) {
        for (const it of b.items || []) {
          if (
            it.skimlinks_payment_status === "pending" ||
            it.skimlinks_payment_status === "scheduled" ||
            it.skimlinks_payment_status === "unpaid"
          ) {
            if (it.action_id) s.add(it.action_id);
          }
        }
      }
    }
    return s;
  }, [dailyReports]);

  const confirmIfUnpaid = (actionIds, contextLabel) => {
    const unpaid = (actionIds || []).filter((aid) => skimlinksUnpaidIds.has(aid));
    if (unpaid.length === 0) return true;
    return window.confirm(
      `⚠ ${unpaid.length} of these ${actionIds.length} sales haven't been paid by Skimlinks yet (still pending / scheduled / unpaid in their payment-status report).\n\n` +
        `Approving${contextLabel ? ` ${contextLabel}` : ""} commits you to paying the publisher with money you haven't received yet.\n\n` +
        `Approve anyway?`,
    );
  };
  void confirmIfUnpaid;  // retained for future bulk-approve UI; lint placeholder.

  const revokeDay = async (date, brandName = null) => {
    try {
      await API.post(`/admin/publishers/${id}/revoke-daily`, { date, brand_name: brandName });
      toast.success("Approval revoked");
      fetchDailyReports();
    } catch { toast.error("Failed"); }
  };

  const revokeActions = async (actionIds, label) => {
    if (!actionIds || actionIds.length === 0) return;
    try {
      const { data } = await API.post(`/admin/publishers/${id}/revoke-actions`, { action_ids: actionIds });
      toast.success(`Revoked ${data.removed}${label ? ` (${label})` : ""}`);
      fetchDailyReports();
      setSelectedActionIds(new Set());
    } catch { toast.error("Failed to revoke"); }
  };

  // ─── STATUS CHANGES (pause / activate / approve / reject) ───
  const changeStatus = async (action) => {
    const confirmText = {
      pause: "Pause this publisher? They won't be able to log in.",
      activate: "Re-activate this publisher?",
      approve: "Approve this publisher? They'll receive an activation email.",
      reject: "Reject this publisher? This cannot be undone.",
    }[action];
    if (!window.confirm(confirmText)) return;
    try {
      await API.post(`/admin/publishers/${id}/${action}`);
      toast.success(
        action === "pause" ? "Publisher paused" :
        action === "activate" ? "Publisher activated" :
        action === "approve" ? "Publisher approved — email sent" :
        "Publisher rejected"
      );
      bumpAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  if (loading) {
    return (
      <div className="p-8" data-testid="admin-publisher-detail-loading">
        <RSpinBlock label="Loading publisher" height={400} data-testid="publisher-detail-spinner" />
      </div>
    );
  }
  if (!detail) return <div className="p-8 text-sm text-red-500">Publisher not found</div>;

  const { publisher, assigned_brands_count = 0, bank_details, traffic_remaps = [] } = detail;

  const totalApproved = dailyReports.filter(d => d.all_approved).length;
  const totalDays = dailyReports.length;

  return (
    <div className="p-8" data-testid="admin-publisher-detail">
      <button onClick={() => navigate("/admin/publishers")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-publishers-btn">
        <ArrowLeft size={16} /> Back to Publishers
      </button>

      <PublisherHeader
        publisher={publisher}
        publisherDefaultSplit={publisherDefaultSplit}
        onChangeStatus={changeStatus}
        onUpdateDefaultSplit={updateDefaultSplit}
        onToggleDiscloseNetworks={toggleDiscloseNetworks}
        onToggleDisabledNetwork={toggleDisabledNetwork}
        onUpdateTimezone={updateTimezone}
        onUpdateClickCap={updateClickCap}
        splitHistory={splitHistory}
      />

      <TrackerSlotsPanel
        publisher={publisher}
        slots={slots}
        slotsLoading={slotsLoading}
        creatingSlot={creatingSlot}
        onAddSlot={addSlot}
        onToggleSlot={toggleSlot}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="border-b border-slate-200 bg-transparent h-auto p-0 mb-6 rounded-none">
          <TabsTrigger value="assign-brands" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium" data-testid="tab-assign-brands">
            Assign Brands ({Number(assigned_brands_count).toLocaleString()})
          </TabsTrigger>
          <TabsTrigger value="reports" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium" data-testid="tab-reports">
            Reports Approval
          </TabsTrigger>
          <TabsTrigger value="postback-logs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium" data-testid="tab-postback-logs">
            Postback Logs
          </TabsTrigger>
        </TabsList>

        {/* ═══ ASSIGN BRANDS TAB ═══ */}
        <TabsContent value="assign-brands" className="space-y-6">
          {/* Smart Add — one-click bulk assign every rate-bearing brand
              from the currently-selected network (or all 3 core networks
              when the pill is "All"). Always passthrough at the
              publisher's configured default split (100% / no silent
              shave by default — admin can override per-brand). Hidden
              assigned brands stay hidden — re-running this after a
              catalog sync is a safe no-op for already-covered brands. */}
          <div
            className="border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white rounded-md p-4 flex items-start gap-3"
            data-testid="smart-add-banner"
          >
            <div className="shrink-0 w-10 h-10 rounded-md bg-emerald-100 flex items-center justify-center">
              <span className="text-emerald-700 text-lg font-bold">⚡</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900">
                Smart Add — auto-assign every brand with a rate
                {lookupNetwork !== "all" && (
                  <> <span className="font-mono text-xs uppercase tracking-wider text-emerald-700 ml-1">[{NETWORK_LABELS_SMART[lookupNetwork] || lookupNetwork}]</span></>
                )}
              </div>
              <div className="text-xs text-emerald-800/80 mt-0.5">
                {lookupNetwork === "all" ? (
                  <>
                    Adds every Impact / Skimlinks / mrge brand that has a
                    usable commission rate and isn&apos;t already assigned to this
                    publisher.
                  </>
                ) : lookupNetwork === "impact" ? (
                  <>
                    Adds every <strong>Impact</strong> brand with a usable commission
                    rate that isn&apos;t already assigned. Impact rates come from
                    synced contracts — brands showing &ldquo;—&rdquo; in the payout
                    column haven&apos;t been synced yet (use Sync from Impact on the
                    brand contract).
                  </>
                ) : (
                  <>
                    Adds every <strong>{NETWORK_LABELS_SMART[lookupNetwork] || lookupNetwork}</strong> brand
                    with a usable commission rate that isn&apos;t already assigned
                    to this publisher.
                  </>
                )}
                {" "}Always passthrough at the publisher&apos;s configured default split (
                <strong>{publisherDefaultSplit}%</strong> to pub
                {publisherDefaultSplit === 100 ? " — no margin retained" : `, ${(100 - publisherDefaultSplit)}% margin retained`}
                ). Per-brand cap overrides via the pencil edit on each row. Cashback brands are skipped — their commission has to go back
                to the end consumer, leaving no margin to split. Safe to re-run.
                <span className="block mt-1 text-emerald-700/70">
                  Tip: switch the network pill below to scope this to a single feed partner.
                </span>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 shrink-0"
              disabled={smartAdding}
              onClick={smartAssign}
              data-testid="smart-add-btn"
            >
              {smartAdding
                ? "Adding…"
                : lookupNetwork === "all"
                  ? "Smart Add all"
                  : `Smart Add ${NETWORK_LABELS_SMART[lookupNetwork] || lookupNetwork}`}
            </Button>
          </div>

          <AdvertiserLookup
            publisherId={id}
            refreshKey={assignmentsEpoch}
            defaultSplit={publisherDefaultSplit}
            onAssignSelected={handleLookupAssign}
            onAssignAllFiltered={handleLookupAssign}
            onNetworkChange={setLookupNetwork}
          />

          <RoutedTrafficPanel
            publisherId={id}
            remaps={traffic_remaps}
            onChanged={bumpAssignments}
          />

          <AssignedBrandsTable
            publisherId={id}
            refreshKey={assignmentsEpoch}
            publisherDefaultSplit={publisherDefaultSplit}
            editingComm={editingComm}
            editForm={editForm}
            setEditForm={setEditForm}
            setEditingComm={setEditingComm}
            setEditingPassthrough={setEditingPassthrough}
            onUpdateCommission={updateCommission}
            onToggleBrandStatus={toggleBrandStatus}
            onChanged={bumpAssignments}
            editingSplitBrandId={editingSplitBrandId}
            editingSplitValue={editingSplitValue}
            setEditingSplitBrandId={setEditingSplitBrandId}
            setEditingSplitValue={setEditingSplitValue}
            onSaveBrandSplit={saveBrandSplit}
            savingSplit={savingSplit}
          />

          <BrandCoveragePanel
            publisherId={id}
            publisherEmail={publisher?.email}
            onAssignmentsChanged={bumpAssignments}
          />

          <ApiPreviewPanel publisherId={id} />

          {bank_details && (
            <div className="border border-slate-200 rounded-md p-5">
              <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">Bank Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Beneficiary:</span> <span className="text-slate-900 font-medium ml-2">{bank_details.beneficiary_name}</span></div>
                <div><span className="text-slate-500">Bank:</span> <span className="text-slate-900 font-medium ml-2">{bank_details.bank_name}</span></div>
                <div><span className="text-slate-500">Account:</span> <span className="text-slate-900 font-mono ml-2">{bank_details.account_number}</span></div>
                <div><span className="text-slate-500">Country:</span> <span className="text-slate-900 font-medium ml-2">{bank_details.beneficiary_country}</span></div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ═══ REPORTS APPROVAL TAB ═══ */}
        <TabsContent value="reports">
          <DailyReportsApproval
            dailyReports={dailyReports}
            loadingReports={loadingReports}
            syncing={syncing}
            totalApproved={totalApproved}
            totalDays={totalDays}
            autoApproveEnabled={publisher?.auto_approve_actions !== false}
            dateRange={reportsRange}
            onDateRangeChange={setReportsRange}
            expandedDate={expandedDate}
            setExpandedDate={(d) => {
              setExpandedDate(d);
              if (d) fetchDayItems(d);
            }}
            dayItemsCache={dayItemsCache}
            dayLoading={dayLoading}
            selectedActionIds={selectedActionIds}
            setSelectedActionIds={setSelectedActionIds}
            onSync={syncActionsFromImpact}
            onRefresh={fetchDailyReports}
            onRevokeDay={revokeDay}
            onRevokeActions={revokeActions}
          />
        </TabsContent>

        {/* ═══ POSTBACK LOGS TAB ═══ */}
        <TabsContent value="postback-logs" className="space-y-4">
          <PostbackLogsTab
            publisherId={id}
            postbackLogs={postbackLogs}
            loadingLogs={loadingLogs}
            logsNetwork={logsNetwork}
            setLogsNetwork={setLogsNetwork}
            logsStatus={logsStatus}
            setLogsStatus={setLogsStatus}
            onRefresh={fetchPostbackLogs}
            page={logsPage}
            totalPages={logsTotalPages}
            total={logsTotal}
            onPageChange={setLogsPage}
          />
        </TabsContent>
      </Tabs>

      <PassthroughEditDialog
        editingPassthrough={editingPassthrough}
        ptActions={ptActions}
        setPtActions={setPtActions}
        ptHeadline={ptHeadline}
        setPtHeadline={setPtHeadline}
        ptSplit={ptSplit}
        setPtSplit={setPtSplit}
        ptFlat={ptFlat}
        setPtFlat={setPtFlat}
        ptCapMode={ptCapMode}
        setPtCapMode={setPtCapMode}
        ptCapPct={ptCapPct}
        setPtCapPct={setPtCapPct}
        ptSaving={ptSaving}
        onClose={closePassthroughDialog}
        onSave={saveEditedPassthrough}
      />
    </div>
  );
}
